import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  UsersIcon,
  UserIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ConfirmModal from '../components/common/ConfirmModal';
import Dropdown from '../components/ui/Dropdown';
import MultiSelect from '../components/ui/MultiSelect';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import TeamService from '../services/team.service';
import toast from '../utils/toast';

const Teams = () => {
  const { } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive'
  const [viewMode, setViewMode] = useState('grid'); // grid or table
  const [formData, setFormData] = useState({
    team_name: '',
    team_lead_email: '',
    team_member_emails: []
  });
  const [teamLeads, setTeamLeads] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);

  useEffect(() => {
    fetchTeams();
    fetchCreateTeamData();
  }, [statusFilter]); // Re-fetch when status filter changes

  const combinedSearch = headerSearch || searchTerm;
  const pagination = useListPagination(filteredTeams, JSON.stringify([combinedSearch, statusFilter]));

  useEffect(() => {
    filterTeams();
  }, [teams, searchTerm, headerSearch]);

  const fetchTeams = async () => {
    try {
      const response = await TeamService.getTeams(statusFilter);
      const transformedTeams = (response || []).map(team => ({
        id: team.id,
        name: team.team_name,
        description: team.description || 'No description available',
        manager: team.teamLead?.name || 'No team lead assigned',
        teamLeadId: team.teamLead?.email || '',
        members: (team.teamMembers || []).map(member => member.name),
        memberCount: (team.teamMembers || []).length,
        department: team.department || 'General',
        status: team.status || 'active',
        teamMembers: team.teamMembers || []
      }));
      setTeams(transformedTeams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load teams';
      toast.error(errorMessage);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const filterTeams = () => {
    let filtered = teams;

    if (combinedSearch) {
      filtered = filtered.filter(team =>
        team.name.toLowerCase().includes(combinedSearch.toLowerCase()) ||
        team.manager.toLowerCase().includes(combinedSearch.toLowerCase()) ||
        team.members.some(member => member.toLowerCase().includes(combinedSearch.toLowerCase()))
      );
    }

    setFilteredTeams(filtered);
  };

  const fetchCreateTeamData = async () => {
    try {
      const data = await TeamService.getCreateTeamData();
      setTeamLeads(data.teamLeads || []);
      setAvailableMembers(data.teamMembers || []);
    } catch (error) {
      console.error('Error fetching team creation data:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load team data';
      toast.error(errorMessage);
      setTeamLeads([]);
      setAvailableMembers([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.team_name.trim()) {
      toast.error('Team name is required');
      return;
    }
    if (!formData.team_lead_email) {
      toast.error('Team lead is required');
      return;
    }
    if (!formData.team_member_emails || formData.team_member_emails.length === 0) {
      toast.error('At least one team member is required');
      return;
    }

    try {
      const payload = {
        team_name: formData.team_name.trim(),
        team_lead_email: formData.team_lead_email,
        team_member_emails: formData.team_member_emails
      };

      if (editingTeam) {
        await TeamService.updateTeam(editingTeam.id, payload);
        toast.success('Team updated successfully');
      } else {
        await TeamService.createTeam(payload);
        toast.success('Team created successfully');
      }

      setShowForm(false);
      setEditingTeam(null);
      setFormData({ team_name: '', team_lead_email: '', team_member_emails: [] });
      fetchTeams();
      fetchCreateTeamData(); // Refresh available team leads
    } catch (error) {
      console.error('Error saving team:', error);
      const errorMessage = error.message || error.response?.data?.error || 'Failed to save team';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    const memberEmails = (team.teamMembers || []).map(member => member.email).filter(email => email);
    setFormData({
      team_name: team.name || team.team_name || '',
      team_lead_email: team.teamLeadId || team.teamLead?.email || '',
      team_member_emails: memberEmails
    });
    
    // Fetch team leads for editing (includes current team lead)
    fetchEditTeamData(team.id);
    setShowForm(true);
  };

  const fetchEditTeamData = async (teamId) => {
    try {
      const data = await TeamService.getEditTeamData(teamId);
      setTeamLeads(data.teamLeads || []);
      setAvailableMembers(data.teamMembers || []);
    } catch (error) {
      console.error('Error fetching edit team data:', error);
      // Fallback to regular create team data
      fetchCreateTeamData();
    }
  };

  const handleDelete = (teamId) => {
    setTeamToDelete(teamId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!teamToDelete) return;
    
    try {
      await TeamService.deleteTeam(teamToDelete);
      toast.success('Team deactivated successfully');
      fetchTeams();
      fetchCreateTeamData(); // Refresh team leads list to make the deactivated team's lead available
    } catch (error) {
      console.error('Error deactivating team:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to deactivate team';
      toast.error(errorMessage);
    } finally {
      setTeamToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Teams Management</h1>
              <p className="text-pink-100">Organize and manage team structures</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{teams.length}</div>
              <div className="text-sm text-pink-200">Total Teams</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters and Search */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Status Filter Dropdown */}
              <Dropdown
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'all', label: 'All' }
                ]}
                placeholder="Filter by status"
                className="w-40"
              />

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Table
                </button>
              </div>

              {/* Create Team Button */}
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors font-medium"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Create Team</span>
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {filteredTeams.length} matching teams out of {teams.length}
        </p>
        {combinedSearch && (
          <button
            onClick={() => setSearchTerm('')}
            className="px-4 py-2 text-sm font-medium text-pink-700 bg-pink-50 border border-pink-200 rounded-lg hover:bg-pink-100 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
      {/* Teams Grid/Table */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pagination.items.map((team, index) => (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <Card.Body className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                        <BuildingOfficeIcon className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                          {team.status === 'inactive' && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{team.department}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {team.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleEdit(team)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(team.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">{team.description}</p>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Team Lead</p>
                        <p className="font-medium">{team.manager}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <UsersIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Team Members</p>
                        <p className="font-medium">{team.memberCount} members</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2">
                      {team.members.slice(0, 3).map((member, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {member}
                        </span>
                      ))}
                      {team.members.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          +{team.members.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Lead
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagination.items.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                          <BuildingOfficeIcon className="w-6 h-6 text-pink-600" />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">{team.name}</div>
                            {team.status === 'inactive' && (
                              <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{team.department}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {team.manager}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {team.memberCount} members
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {team.status === 'active' ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(team)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(team.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">No actions</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ListPagination {...pagination} label="teams" disabled={loading} />

      {filteredTeams.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <BuildingOfficeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No teams found</h3>
          <p className="text-gray-600 mb-4">
            {combinedSearch
              ? 'Try adjusting your search criteria'
              : 'Get started by creating your first team'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Create Team</span>
          </button>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTeamToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Deactivate Team"
        message="Are you sure you want to deactivate this team? The team will be set to inactive status."
        confirmText="OK"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Team Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {editingTeam ? 'Edit Team' : 'Create New Team'}
              </h2>
              <p className="text-gray-600 text-sm">
                {editingTeam ? 'Update team information and members' : 'Set up a new team with team lead and members'}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.team_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, team_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  placeholder="Enter team name (e.g., Development Team)"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Lead <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={formData.team_lead_email}
                  onChange={(value) => setFormData(prev => ({ ...prev, team_lead_email: value }))}
                  options={teamLeads.map(teamLead => ({
                    value: teamLead.email,
                    label: teamLead.name
                  }))}
                  placeholder="Select team lead"
                  searchable
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only employees with team lead role are shown
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Members <span className="text-red-500">*</span>
                </label>
                <MultiSelect
                  value={formData.team_member_emails}
                  onChange={(value) => setFormData(prev => ({ ...prev, team_member_emails: value }))}
                  options={availableMembers
                    .filter(member => member.email !== formData.team_lead_email)
                    .map(member => ({
                      value: member.email,
                      label: member.name
                    }))}
                  placeholder="Select team members"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  All employees are available. Employees can be in multiple teams. Selected team lead is excluded.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors font-medium"
                >
                  {editingTeam ? 'Update Team' : 'Create Team'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTeam(null);
                    setFormData({ team_name: '', team_lead_email: '', team_member_emails: [] });
                  }}
                  className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Teams;
